import { useState, useEffect } from 'react';
import { useLang } from '../context/LanguageContext';
import { fetchArticles } from '../services/api';

function formatDate(dateStr, lang) {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString(lang === 'fr' ? 'fr-FR' : 'en-US', { year: 'numeric', month: 'long', day: 'numeric' });
}

export default function News() {
  const { lang } = useLang();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchArticles()
      .then(setArticles)
      .catch(() => setError(lang === 'fr' ? 'Impossible de charger les actualités.' : 'Unable to load news.'))
      .finally(() => setLoading(false));
  }, [lang]);

  return (
    <>
      <section className="hero" style={{ background: 'linear-gradient(135deg,var(--primary) 0%,var(--secondary) 100%)', padding: '6rem 0 4rem' }}>
        <div className="container">
          <div style={{ textAlign: 'center', color: 'white' }} className="fade-in-up">
            <span style={{ display: 'inline-block', padding: '8px 20px', background: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 700, fontSize: '0.875rem', borderRadius: 20, marginBottom: '1.5rem', textTransform: 'uppercase', letterSpacing: 1 }}>WACEAS</span>
            <h1 style={{ fontSize: '3rem', color: 'white', marginBottom: '1rem' }}>
              {lang === 'fr' ? 'Actualités' : 'News & Updates'}
            </h1>
            <p style={{ fontSize: '1.125rem', opacity: 0.9 }}>
              {lang === 'fr' ? 'Dernières nouvelles et annonces' : 'Latest stories and announcements'}
            </p>
          </div>
        </div>
      </section>

      <section>
        <div className="container">
          {loading && (
            <div style={{ textAlign: 'center', padding: '5rem', color: 'rgba(26,26,26,0.5)' }}>
              <i className="fas fa-spinner fa-spin" style={{ fontSize: '2.5rem', display: 'block', marginBottom: '1rem' }}></i>
            </div>
          )}
          {error && (
            <div style={{ textAlign: 'center', padding: '5rem', color: 'var(--secondary)' }}>
              <i className="fas fa-exclamation-circle" style={{ fontSize: '2.5rem', display: 'block', marginBottom: '1rem' }}></i>
              {error}
            </div>
          )}
          {!loading && !error && articles.length === 0 && (
            <div style={{ textAlign: 'center', padding: '5rem', color: 'rgba(26,26,26,0.5)' }}>
              <i className="fas fa-newspaper" style={{ fontSize: '3rem', display: 'block', marginBottom: '1rem' }}></i>
              {lang === 'fr' ? 'Aucune actualité pour le moment.' : 'No news yet.'}
            </div>
          )}
          {!loading && articles.length > 0 && (
            <div style={{ display: 'grid', gap: '2rem', maxWidth: 900, margin: '0 auto' }}>
              {articles.map(article => (
                <div key={article.id} className="card fade-in-up" style={{ display: 'flex', gap: '2rem', padding: '1.5rem', alignItems: 'flex-start', flexWrap: 'wrap' }}>
                  {article.cover_image && (
                    <img src={article.cover_image} alt={article.title} style={{ width: 180, height: 130, objectFit: 'cover', borderRadius: 8, flexShrink: 0 }} />
                  )}
                  <div style={{ flex: 1, minWidth: 200 }}>
                    {article.category && (
                      <span style={{ fontSize: '0.75rem', color: 'var(--primary)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>{article.category}</span>
                    )}
                    <h3 style={{ margin: '0.4rem 0 0.6rem', fontSize: '1.2rem', lineHeight: 1.4 }}>{article.title}</h3>
                    {article.summary && (
                      <p style={{ color: 'rgba(26,26,26,0.7)', marginBottom: '1rem', lineHeight: 1.6, fontSize: '0.95rem' }}>{article.summary}</p>
                    )}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem' }}>
                      {article.published_at && (
                        <span style={{ fontSize: '0.82rem', color: 'rgba(26,26,26,0.45)' }}>
                          <i className="fas fa-calendar-alt" style={{ marginRight: '0.4rem' }}></i>
                          {formatDate(article.published_at, lang)}
                        </span>
                      )}
                      <button className="btn-outline" style={{ padding: '8px 18px', fontSize: '0.88rem' }} onClick={() => setSelected(article)}>
                        {lang === 'fr' ? 'Lire la suite' : 'Read more'}&nbsp;<i className="fas fa-arrow-right"></i>
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {selected && (
        <div onClick={() => setSelected(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 9999, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '2rem', overflowY: 'auto' }}>
          <div onClick={e => e.stopPropagation()} style={{ background: 'white', borderRadius: 12, maxWidth: 720, width: '100%', padding: '2.5rem', position: 'relative', margin: 'auto' }}>
            <button onClick={() => setSelected(null)} style={{ position: 'absolute', top: '1rem', right: '1rem', background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: 'rgba(26,26,26,0.4)', width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '50%' }}>
              <i className="fas fa-times"></i>
            </button>
            {selected.cover_image && (
              <img src={selected.cover_image} alt={selected.title} style={{ width: '100%', height: 260, objectFit: 'cover', borderRadius: 8, marginBottom: '1.5rem' }} />
            )}
            {selected.category && (
              <span style={{ fontSize: '0.75rem', color: 'var(--primary)', fontWeight: 700, textTransform: 'uppercase' }}>{selected.category}</span>
            )}
            <h2 style={{ margin: '0.5rem 0 0.75rem', lineHeight: 1.3 }}>{selected.title}</h2>
            {selected.published_at && (
              <p style={{ color: 'rgba(26,26,26,0.45)', fontSize: '0.85rem', marginBottom: '1.5rem' }}>
                <i className="fas fa-calendar-alt" style={{ marginRight: '0.4rem' }}></i>
                {formatDate(selected.published_at, lang)}
                {selected.author && ` · ${selected.author}`}
              </p>
            )}
            <div style={{ lineHeight: 1.85, color: 'rgba(26,26,26,0.82)', whiteSpace: 'pre-wrap', fontSize: '1rem' }}>{selected.content}</div>
          </div>
        </div>
      )}
    </>
  );
}
